import type { PayloadAction } from '@reduxjs/toolkit';
import { createAppSlice } from 'creators';
import { Toast } from '@10d/tend-ui/primitives/Toast';

import { get, post } from 'api/requests';

import { AppThunk } from 'store/types';
import { IParamsReq } from 'types';
import {
  IAnalyticsTransferToSettlementCommonData,
  IAnalyticsTransferToSettlementNetworks,
  IAnalyticsTransferToSettlementNonResidential,
  IAnalyticsTransferToSettlementResidential,
} from 'features/Analytics/AnalyticsTransferToSettlement/types';

const initialState: IAnalyticsTransferToSettlementStore = {
  common: null,
  commonLoading: false,
  loadExcelDownload: false,
  networks: null,
  networksLoading: false,
  nonResidential: null,
  nonResidentialLoading: false,
  requestId: undefined,
  residential: null,
  residentialLoading: false,
};

export const analyticsTransferToSettlementSlice = createAppSlice({
  name: 'analyticsTransferToSettlementSlice',
  initialState,
  reducers: create => ({
    reset: create.reducer(() => initialState),

    residentialReset: create.reducer(state => {
      return { ...state, residential: null, residentialLoading: false };
    }),

    nonResidentialReset: create.reducer(state => {
      return { ...state, nonResidential: null, nonResidentialLoading: false };
    }),

    networksReset: create.reducer(state => {
      return { ...state, networks: null, networksLoading: false };
    }),

    loadExcelDownload: create.reducer((state, action: PayloadAction<boolean>) => {
      return { ...state, loadExcelDownload: action.payload };
    }),

    getCommonData: create.asyncThunk(
      async (params: IParamsReq, thinkApi) => {
        try {
          const { data } = await get<IAnalyticsTransferToSettlementCommonData>(
            '/api/v2/analytics/transfer-to-settlement/common/',
            params,
          );
          return data;
        } catch (error) {
          return thinkApi.rejectWithValue(error);
        }
      },
      // Обработка состояний запроса getCommonData
      {
        pending: (state, { meta }) => {
          return { ...state, requestId: meta.requestId, commonLoading: true };
        },
        rejected: (state, { meta }) => {
          if (state.requestId === meta.requestId) {
            state.commonLoading = false;
          }
        },
        fulfilled: (state, { payload }) => {
          state.commonLoading = false;
          state.requestId = undefined;

          if (payload) {
            state.common = payload;
          }
        },
      },
    ),

    // getResidentialData
    getResidentialData: create.asyncThunk(
      async (params: IParamsReq, thinkApi) => {
        try {
          const { data } = await get<IAnalyticsTransferToSettlementResidential>(
            '/api/v2/analytics/transfer-to-settlement/residential/',
            params,
          );
          return data;
        } catch (error) {
          return thinkApi.rejectWithValue(error);
        }
      },
      // Обработка состояний запроса getResidentialData
      {
        pending: (state, { meta }) => {
          return { ...state, requestId: meta.requestId, residentialLoading: true };
        },
        rejected: (state, { meta }) => {
          if (state.requestId === meta.requestId) {
            state.residentialLoading = false;
          }
        },
        fulfilled: (state, { payload }) => {
          state.residentialLoading = false;
          state.requestId = undefined;

          if (payload) {
            state.residential = payload;
          }
        },
      },
    ),

    // getNonResidentialData
    getNonResidentialData: create.asyncThunk(
      async (params: IParamsReq, thinkApi) => {
        try {
          const { data } = await get<IAnalyticsTransferToSettlementNonResidential>(
            '/api/v2/analytics/transfer-to-settlement/non_residential/',
            params,
          );
          return data;
        } catch (error) {
          return thinkApi.rejectWithValue(error);
        }
      },
      // Обработка состояний запроса getResidentialData
      {
        pending: (state, { meta }) => {
          return { ...state, requestId: meta.requestId, nonResidentialLoading: true };
        },
        rejected: (state, { meta }) => {
          if (state.requestId === meta.requestId) {
            state.nonResidentialLoading = false;
          }
        },
        fulfilled: (state, { payload }) => {
          state.nonResidentialLoading = false;
          state.requestId = undefined;

          if (payload) {
            state.nonResidential = payload;
          }
        },
      },
    ),

    // getNetworksData
    getNetworksData: create.asyncThunk(
      async (params: IParamsReq, thinkApi) => {
        try {
          const { data } = await get<IAnalyticsTransferToSettlementNetworks>(
            '/api/v2/analytics/transfer-to-settlement/networks/',
            params,
          );
          return data;
        } catch (error) {
          return thinkApi.rejectWithValue(error);
        }
      },
      // Обработка состояний запроса getResidentialData
      {
        pending: (state, { meta }) => {
          return { ...state, requestId: meta.requestId, networksLoading: true };
        },
        rejected: (state, { meta }) => {
          if (state.requestId === meta.requestId) {
            state.networksLoading = false;
          }
        },
        fulfilled: (state, { payload }) => {
          state.networksLoading = false;
          state.requestId = undefined;

          if (payload) {
            state.networks = payload;
          }
        },
      },
    ),
  }),
});

export const getDownloadExcel =
  (search: IParamsReq): AppThunk =>
  dispatch => {
    dispatch(setLoadExcelDownload(true));

    Toast.success({
      message: 'Файл отправлен на почту',
    });

    const url = `/api/v2/analytics/transfer-to-settlement/excel-report/?project=${search.project || ''}&actual_queue=${search.actual_queue || ''}&structure=${search.structure || ''}&date=${search.date || ''}`;
    post(url).finally(() => {
      dispatch(setLoadExcelDownload(false));
    });
  };

export const {
  // getDownloadExcel,
  getCommonData,
  getNetworksData,
  getNonResidentialData,
  getResidentialData,
  loadExcelDownload: setLoadExcelDownload,
  networksReset: setNetworksReset,
  nonResidentialReset: setNonResidentialReset,
  reset: setReset,
  residentialReset: setResidentialReset,
} = analyticsTransferToSettlementSlice.actions;

interface IAnalyticsTransferToSettlementStore {
  common: IAnalyticsTransferToSettlementCommonData | null;
  commonLoading: boolean;
  loadExcelDownload: boolean;
  networks: IAnalyticsTransferToSettlementNetworks | null;
  networksLoading: boolean;
  nonResidential: IAnalyticsTransferToSettlementNonResidential | null;
  nonResidentialLoading: boolean;
  requestId: string | undefined;
  residential: IAnalyticsTransferToSettlementResidential | null;
  residentialLoading: boolean;
}
